import React from "react";
import { reminderStatus, REMINDER_DOT_CLASS } from "@/lib/reminder-status";
import { computeClassBreakdown, studentCalendarLabelMonth, isExamAppointment, unavailColor, examTextClass } from "@/lib/utils-app";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const STATUS_DOT = {
  pending: "bg-amber-500",
  confirmed: "bg-green-500",
  cancelled: "bg-red-500",
  change_requested: "bg-blue-500",
};

function pad(n) {
  return String(n).padStart(2, "0");
}

export function isoFromDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function buildMonthGrid(year, month) {
  // Monday-first 6×7 grid covering the whole month.
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function DayCell({ date, inMonth, isToday, isPast, isSelected, appointments, unavailabilities, milestones, onSelect }) {
  const iso = isoFromDate(date);
  const unavail = unavailabilities || [];
  // V1.43 · ¿El día está bloqueado por completo (vacaciones/ausencia)? En ese
  // caso pintamos toda la celda en gris con un mensaje prominente para que el
  // ojo detecte al instante que no se pueden asignar clases.
  const allDayBlock = unavail.find((u) => u.all_day) || null;
  // V1.48.1 · Timeline unificado: bloqueos y sesiones intercalados por hora
  // para que el ojo lea el día en orden real (bloqueo día completo → 00:00).
  const merged = [
    ...(milestones || []).map((m) => ({ kind: "ciclo", time: "07:00", m })),
    ...unavail.filter((u) => !u.all_day).map((u) => ({ kind: "unavail", time: u.time || "00:00", u })),
    ...appointments.map((a) => ({ kind: "appt", time: a.time || "00:00", a })),
  ].sort((x, y) => x.time.localeCompare(y.time));
  const visibleMerged = allDayBlock ? [] : merged.slice(0, 6);
  const extras = allDayBlock ? merged.length : merged.length - visibleMerged.length;
  const reminderCounts = appointments.reduce(
    (acc, a) => {
      const r = reminderStatus(a);
      if (r === "red") acc.red += 1;
      else if (r === "orange") acc.orange += 1;
      return acc;
    },
    { red: 0, orange: 0 }
  );
  return (
    <button
      data-testid={`cal-day-${iso}`}
      onClick={(e) => onSelect(iso, e)}
      className={
        "relative text-left min-h-[100px] sm:min-h-[150px] border-b border-r border-neutral-100 p-1.5 sm:p-2 transition-colors " +
        (isToday
          ? "bg-green-100 hover:bg-green-200/70 "
          : allDayBlock
          ? "bg-neutral-200/70 hover:bg-neutral-200 "
          : isPast
          ? "bg-neutral-200/70 hover:bg-neutral-200 "
          : isSelected ? "bg-orange-50 " : "hover:bg-neutral-50 ") +
        (inMonth ? "" : "text-neutral-300 ")
      }
      data-all-day-block={allDayBlock ? "1" : "0"}
    >
      {inMonth && (reminderCounts.red > 0 || reminderCounts.orange > 0) && (
        <span
          data-testid={`cal-day-reminder-badge-${iso}`}
          className={
            "absolute top-1 right-1 h-2.5 w-2.5 rounded-full ring-2 ring-white " +
            (reminderCounts.red > 0 ? "bg-red-500 animate-pulse" : "bg-orange-500")
          }
          title={
            reminderCounts.red > 0
              ? `${reminderCounts.red} recordatorio(s) urgentes (<24h)`
              : `${reminderCounts.orange} recordatorio(s) pendientes (24-48h)`
          }
        />
      )}
      <div className="flex items-center justify-between mb-1">
        <span
          className={
            "inline-flex items-center justify-center text-xs font-semibold " +
            (isToday
              ? "bg-orange-500 text-white rounded-full h-6 w-6"
              : inMonth
              ? "text-neutral-800"
              : "")
          }
        >
          {date.getDate()}
        </span>
        {appointments.length > 0 && inMonth && (
          <span className="text-[10px] font-semibold text-neutral-500">
            {appointments.length}
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        {/* V1.43 · Bloqueo de día completo (vacaciones/ausencia) —
            mensaje prominente sobre fondo gris que cubre toda la celda. */}
        {allDayBlock && (
          <div
            className={"flex items-center gap-1 text-[10px] sm:text-xs leading-tight rounded-md px-1.5 py-1 font-semibold truncate " + unavailColor(allDayBlock.color).chip}
            title={`${allDayBlock.teacher || ""} · ${allDayBlock.title || "No disponible"}`}
            data-testid={`cal-unavail-allday-${allDayBlock.id}`}
            data-color={allDayBlock.color || "gris"}
          >
            <span className={"inline-block h-1.5 w-1.5 rounded-full shrink-0 " + unavailColor(allDayBlock.color).dot} />
            <span className="truncate">{allDayBlock.title || "No disponible"}</span>
          </div>
        )}
        {/* V1.48.1 · Bloques y sesiones intercalados en orden cronológico. */}
        {!allDayBlock && visibleMerged.map((entry) => {
          if (entry.kind === "ciclo") {
            const m = entry.m;
            return (
              <div
                key={`ciclo-${m.id}`}
                className="flex items-center gap-1 text-[10px] sm:text-xs leading-tight truncate rounded-md bg-red-600 text-white px-1.5 py-0.5 font-bold"
                title={`07:00 · CICLO DGT · ${m.title}${m.ciclo_number ? ` (Ciclo ${m.ciclo_number})` : ""} · límite ${m.time_limit || "23:59"}`}
                data-testid={`cal-ciclo-line-${m.id}`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0 bg-white animate-pulse" />
                <span className="truncate">
                  <span className="font-bold tabular-nums">07:00</span>{" "}
                  <span className="hidden sm:inline">{m.title}{m.ciclo_number ? ` · C.${m.ciclo_number}` : ""}</span>
                </span>
              </div>
            );
          }
          if (entry.kind === "unavail") {
            const u = entry.u;
            return (
              <div
                key={u.id}
                className={"flex items-center gap-1 text-[10px] sm:text-xs leading-tight truncate rounded-md px-1.5 py-0.5 font-semibold " + unavailColor(u.color).chip}
                title={`${u.time}${u.end_time ? `–${u.end_time}` : ""} · ${u.teacher || ""} · ${u.title || "No disponible"}`}
                data-testid={`cal-unavail-line-${u.id}`}
                data-color={u.color || "gris"}
              >
                <span className={"inline-block h-1.5 w-1.5 rounded-full shrink-0 " + unavailColor(u.color).dot} />
                <span className="truncate">
                  <span className="font-semibold tabular-nums">
                    {u.time}
                    {u.end_time && (
                      <span className={"font-normal " + unavailColor(u.color).muted}>–{u.end_time}</span>
                    )}
                  </span>{" "}
                  <span className="italic hidden sm:inline opacity-90">
                    {u.title || "No disponible"}
                  </span>
                </span>
              </div>
            );
          }
          const a = entry.a;
          const rem = reminderStatus(a);
          const isExam = isExamAppointment(a.appointment_type);
          const { numClasses } = isExam
            ? { numClasses: 0 }
            : computeClassBreakdown(a.time, a.end_time);
          const shortName = studentCalendarLabelMonth(a.student);
          return (
            <div
              key={a.id}
              className="flex items-center gap-1 text-[10px] sm:text-xs leading-tight truncate"
              title={`${a.time}${a.end_time ? `–${a.end_time}` : ""}${numClasses > 0 ? ` (${numClasses})` : ""} · ${a.student?.name || ""}${rem ? ` · ${rem === "red" ? "Recordatorio <24h" : rem === "orange" ? "Recordatorio 24-48h" : "Recordatorio enviado"}` : ""}`}
              data-testid={`cal-appt-line-${a.id}`}
              data-reminder-status={rem || "none"}
            >
              <span
                className={
                  "inline-block h-1.5 w-1.5 rounded-full shrink-0 " +
                  (STATUS_DOT[a.status] || "bg-neutral-400")
                }
              />
              {rem && (
                <span
                  data-testid={`cal-reminder-dot-${a.id}`}
                  className={"inline-block h-1.5 w-1.5 rounded-full shrink-0 " + REMINDER_DOT_CLASS[rem]}
                />
              )}
              <span className="truncate">
                <span className="font-semibold">
                  {a.time}
                  {a.end_time && (
                    <span className="text-neutral-400 font-normal">–{a.end_time}</span>
                  )}
                </span>{" "}
                <span className={(isExam ? examTextClass(a.appointment_type) + " font-semibold" : "text-neutral-500") + " hidden sm:inline"}>
                  {shortName}
                </span>
                {numClasses > 0 && (
                  <span className="text-orange-600 font-semibold ml-0.5">({numClasses})</span>
                )}
              </span>
            </div>
          );
        })}
        {!allDayBlock && extras > 0 && (
          <div className="text-[10px] text-neutral-400">
            +{extras} más
          </div>
        )}
        {allDayBlock && appointments.length > 0 && (
          <div className="text-[10px] text-neutral-500 italic">
            {appointments.length} sesión{appointments.length === 1 ? "" : "es"} solapada{appointments.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </button>
  );
}

export default function MonthGrid({ cursor, byDate, unavailByDate, milestonesByDate, todayIso, selectedDate, onSelect }) {
  const days = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  return (
    <div className="card-surface overflow-hidden">
      <div className="grid grid-cols-7 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-600">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-center">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const iso = isoFromDate(d);
          return (
            <DayCell
              key={iso}
              date={d}
              inMonth={d.getMonth() === cursor.getMonth()}
              isToday={iso === todayIso}
              isPast={iso < todayIso}
              isSelected={iso === selectedDate}
              appointments={byDate[iso] || []}
              unavailabilities={(unavailByDate && unavailByDate[iso]) || []}
              milestones={(milestonesByDate && milestonesByDate[iso]) || []}
              onSelect={onSelect}
            />
          );
        })}
      </div>
    </div>
  );
}
